import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { unsaveMessage } from '../../store/slices/savedMessageSlice';
import { addNotification } from '../../store/slices/uiSlice';
import { setCurrentChannel } from '../../store/slices/chatSlice';
import ImageViewerModal from '../chat/ImageViewerModal';
import AttachmentGroupBar from '../chat/AttachmentGroupBar';
import AttachmentsListModal from '../chat/AttachmentsListModal';
import { downloadAttachment, openAttachmentFallback } from '../../utils/fileUtils';
import { BookmarkIcon } from '../chat/SidebarIcons';

const SavedMessageItem = ({
  savedMessage,
  channels,
  directMessages,
  onNavigate,
  inChannelPanel = false,
  onMessageClick,
  showChannelName = false,
  channelName,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewingGallery, setViewingGallery] = useState(null);
  const [viewingAllAttachments, setViewingAllAttachments] = useState(null);
  const [savedAttachmentsExpanded, setSavedAttachmentsExpanded] = useState(false);
  const handleUnsave = async (e) => {
    e.stopPropagation();
    try {
      await dispatch(unsaveMessage(savedMessage.messageId)).unwrap();
      dispatch(
        addNotification({
          type: 'success',
            message: 'Bookmark removed',
        })
      );
    } catch (error) {
      // Convert error to string if it's an Error object
      const errorMessage = typeof error === 'string'
        ? error
        : error?.message || error?.toString() || 'Failed to remove bookmark. Please try again.';
      dispatch(
        addNotification({
          type: 'error',
          message: errorMessage,
        })
      );
    }
  };

  const handleClick = () => {
    if (inChannelPanel && onMessageClick && savedMessage.messageId) {
      onMessageClick(savedMessage.messageId);
      return;
    }

    if (savedMessage.channelId && savedMessage.message) {
      // Find the channel
      const allChannels = [...channels, ...directMessages];
      const channel = allChannels.find((ch) => ch.id === savedMessage.channelId);

      if (channel) {
        dispatch(setCurrentChannel(channel));
        // Small delay to ensure Redux state is updated before navigation
        setTimeout(() => {
          navigate(`/chat?messageId=${savedMessage.messageId}`);
          if (onNavigate) {
            onNavigate();
          }
        }, 50);
      }
    }
  };

  const handleDownload = async (attachment, e) => {
    e?.stopPropagation();
    try {
      await downloadAttachment(attachment);
    } catch (error) {
      console.error('Download error:', error);
      openAttachmentFallback(attachment);
    }
  };

  const message = savedMessage.message || {
    id: savedMessage.messageId,
    content: 'Saved message (offline copy)',
    senderName: 'WorkLoop',
    createdAt: savedMessage.createdAt,
    attachments: [],
  };

  const hasContent = message.content && message.content.trim() &&
    message.content.trim() !== '<p><br></p>' &&
    message.content.trim() !== '<p></p>';
  const hasAttachments = message.attachments && message.attachments.length > 0;
  const isHTML = message.content && message.content.trim().startsWith('<') && message.content.includes('</');

  return (
    <>
      <div
        className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer border border-gray-200 dark:border-gray-700"
        onClick={handleClick}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Header with sender and timestamp */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {message.senderName || 'Unknown'}
              </span>
              {showChannelName && channelName && (
                <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                  {channelName}
                </span>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {message.createdAt
                  ? new Date(message.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                  : ''}
              </span>
              {message.isEdited && (
                <span className="text-xs text-gray-400 dark:text-gray-500 italic">(edited)</span>
              )}
            </div>

            {/* Message Content */}
            {hasContent && (
              <div className="mb-3">
                {isHTML ? (
                  <div
                    className="ql-editor-content text-sm text-gray-700 dark:text-gray-300"
                    dangerouslySetInnerHTML={{ __html: message.content }}
                    style={{
                      fontSize: '0.875rem',
                      lineHeight: '1.5',
                    }}
                  />
                ) : (
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {message.content}
                  </p>
                )}
              </div>
            )}

            {/* Attachments */}
            {hasAttachments && (
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                <AttachmentGroupBar
                  attachments={message.attachments}
                  groupName={message.attachmentGroupName || ''}
                  expanded={savedAttachmentsExpanded}
                  onExpandedChange={setSavedAttachmentsExpanded}
                  onViewImage={(images, index) =>
                    setViewingGallery({ images, index })
                  }
                  onDownload={(attachment) => handleDownload(attachment)}
                  onViewAll={(all) => setViewingAllAttachments(all)}
                  mode="display"
                />
              </div>
            )}

            {/* Link Preview */}
            {message.linkPreview && (
              <div className="mt-3 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden max-w-md">
                <a
                  href={message.linkPreview.url ? message.linkPreview.url.replace(/&amp;/g, '&') : message.linkPreview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {message.linkPreview.imageUrl && (
                    <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                      <img
                        src={message.linkPreview.imageUrl}
                        alt={message.linkPreview.title || 'Link preview'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          if (e.target.dataset.fallbackTried === '1') return;
                          e.target.dataset.fallbackTried = '1';
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="p-3">
                    {message.linkPreview.siteName && (
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">
                        {message.linkPreview.siteName}
                      </p>
                    )}
                    {message.linkPreview.title && (
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
                        {message.linkPreview.title}
                      </h4>
                    )}
                    {message.linkPreview.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                        {message.linkPreview.description}
                      </p>
                    )}
                  </div>
                </a>
              </div>
            )}
          </div>

          {/* Unsave Button */}
          <button
            onClick={handleUnsave}
            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            title="Remove bookmark"
            aria-label="Remove bookmark"
          >
            <BookmarkIcon className="w-5 h-5" filled />
          </button>
        </div>
      </div>

      {viewingGallery && (
        <ImageViewerModal
          images={viewingGallery.images}
          initialIndex={viewingGallery.index}
          onClose={() => setViewingGallery(null)}
          onDownload={(attachment) => handleDownload(attachment)}
        />
      )}

      {viewingAllAttachments && (
        <AttachmentsListModal
          attachments={viewingAllAttachments}
          onClose={() => setViewingAllAttachments(null)}
          onViewImage={(images, index) => {
            setViewingAllAttachments(null);
            setViewingGallery({ images, index });
          }}
          onDownload={(attachment) => handleDownload(attachment)}
        />
      )}
    </>
  );
};

export default SavedMessageItem;
